export type DashboardSort = {
  field: "treatmentValue" | "nextAppointment";
  direction: "asc" | "desc";
} | null;

export type DisplayConsentStatus = "Draft" | "Sent for signing" | "Completed" | "Archived";

const normalizeStatus = (value: unknown) =>
  String(value ?? "").trim().toLowerCase().replace(/[_-]+/g, " ");

export const isArchivedRecord = (row: Record<string, any> | null | undefined): boolean =>
  [row, row?.raw].some((record) => record && (
    record.isArchived === true || record.is_archived === true || record.archived === true ||
    Boolean(record.archived_at) ||
    [record.status, record.consent_status, record.consentStatus, record.consent_status_label]
      .some((status) => normalizeStatus(status) === "archived")
  ));

// Keep the API status intact for existing workflows; these are display labels only.
export function getConsentDisplayStatus(row: Record<string, any>): DisplayConsentStatus {
  if (isArchivedRecord(row)) return "Archived";
  const statuses = [row.status, row.consent_status, row.consentStatus, row.consent_status_label]
    .map(normalizeStatus);
  if (row.signed_date || row.signed_at || row.paper_consent_uploaded || row.uploaded_to_dentally_patient_record === true ||
      statuses.some((status) => ["completed", "complete", "signed"].includes(status))) {
    return "Completed";
  }
  if (statuses.includes("draft")) return "Draft";
  if (Number(row.email_sent_count ?? row.emails_count ?? 0) > 0 ||
      statuses.some((status) => ["sent for signing", "sent unsigned", "sent", "pending", "not signed", "expired", "email bounced"].includes(status))) {
    return "Sent for signing";
  }
  return "Draft";
}

function parseAmount(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const amount = typeof value === "number" ? value : Number(value.replace(/[£,\s]/g, ""));
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

export function getTreatmentValue(row: Record<string, any>): number | null {
  for (const value of [row.total_treatment_cost, row.treatment_value, row.treatmentValue,
    row.treatment_plan?.total_treatment_cost, row.treatment_plan?.treatment_value]) {
    const amount = parseAmount(value);
    if (amount !== null) return amount;
  }
  const treatments = row.treatment_plan?.treatments;
  if (!Array.isArray(treatments) || !treatments.length) return null;
  const prices = treatments.map((treatment) => parseAmount(treatment.price));
  return prices.every((price) => price !== null)
    ? prices.reduce<number>((total, price) => total + (price ?? 0), 0) : null;
}

const currency = new Intl.NumberFormat("en-GB", {
  style: "currency", currency: "GBP", maximumFractionDigits: 2, minimumFractionDigits: 0,
});

export const formatFullTreatmentValue = (amount: number | null) =>
  amount === null ? "Treatment value unavailable" : currency.format(amount);

export const formatTreatmentValue = (amount: number | null) => {
  if (amount === null) return "—";
  if (amount < 1000) return currency.format(amount);
  return `£${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(amount / 1000)}K`;
};

function parseAppointmentDate(value: unknown): { date: Date; dateOnly: boolean } | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const text = value.trim();
  const uk = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/.exec(text);
  if (!uk && !iso) return null;
  const year = Number(uk ? uk[3] : iso![1]);
  const month = Number(uk ? uk[2] : iso![2]);
  const day = Number(uk ? uk[1] : iso![3]);
  const calendarDate = new Date(year, month - 1, day);
  if (calendarDate.getFullYear() !== year || calendarDate.getMonth() !== month - 1 || calendarDate.getDate() !== day) return null;
  const dateOnly = Boolean(uk) || text.length === 10;
  const date = dateOnly ? calendarDate : new Date(text.replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : { date, dateOnly };
}

export function getNextAppointment(row: Record<string, any>, now = new Date()): Date | null {
  const candidates: unknown[] = [row.next_appointment_date, row.appointment_date];
  const addRecord = (record: any) => {
    if (!record || typeof record !== "object" || record.cancelled_at || record.deleted_at ||
        record.cancelled === true || record.canceled === true || record.is_cancelled === true ||
        ["cancelled", "canceled", "deleted", "completed", "attended", "no show"].includes(normalizeStatus(record.status))) return;
    candidates.push(record.start_at ?? record.starts_at ?? record.start_time ?? record.appointment_date ?? record.date);
  };
  addRecord(row.appointment);
  for (const records of [row.appointments, row.patient?.appointments, row.treatment_plan?.appointments]) {
    if (Array.isArray(records)) records.forEach(addRecord);
  }
  let treatmentDates = row.treatment_appointment_dates;
  if (typeof treatmentDates === "string") {
    try { treatmentDates = JSON.parse(treatmentDates); } catch { treatmentDates = [treatmentDates]; }
  }
  if (Array.isArray(treatmentDates)) {
    treatmentDates.forEach((value) => typeof value === "object" ? addRecord(value) : candidates.push(value));
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dates = candidates.map(parseAppointmentDate).filter((value): value is NonNullable<typeof value> =>
    value !== null && value.date.getTime() >= (value.dateOnly ? today : now.getTime()));
  dates.sort((a, b) => a.date.getTime() - b.date.getTime());
  return dates[0]?.date ?? null;
}

export function sortDashboardRows<T extends Record<string, any>>(rows: T[], sort: DashboardSort, now = new Date()): T[] {
  if (!sort) return rows;
  const direction = sort.direction === "asc" ? 1 : -1;
  const values = rows.map((row) => ({ row, value: sort.field === "treatmentValue"
    ? getTreatmentValue(row) : getNextAppointment(row, now)?.getTime() ?? null }));
  values.sort((a, b) => {
    if (a.value === null && b.value === null) return 0;
    if (sort.field === "nextAppointment") {
      if (a.value === null) return -direction;
      if (b.value === null) return direction;
    } else {
      if (a.value === null) return 1;
      if (b.value === null) return -1;
    }
    return (a.value! - b.value!) * direction;
  });
  return values.map(({ row }) => row);
}

// The existing endpoints expose pagination, not a documented sorting contract.
// Read all matching pages when sorting so ordering is correct across pagination.
export async function loadDashboardPage(
  request: (params: Record<string, any>) => Promise<any>,
  params: Record<string, any>,
  sort: DashboardSort,
): Promise<any> {
  if (!sort) return request(params);
  const first = await request({ ...params, page: 1, per_page: 50 });
  const readRows = (response: any): Record<string, any>[] =>
    Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
  const rows = [...readRows(first)];
  const total = Number(first.data?.meta?.total ?? rows.length);
  const pageSize = Number(first.data?.meta?.per_page ?? rows.length);
  const lastPage = pageSize > 0 ? Math.ceil(total / pageSize) : 1;
  for (let page = 2; page <= lastPage; page += 4) {
    const responses = await Promise.all(Array.from({ length: Math.min(4, lastPage - page + 1) },
      (_, index) => request({ ...params, page: page + index, per_page: 50 })));
    responses.forEach((response) => rows.push(...readRows(response)));
  }
  const ordered = sortDashboardRows(rows, sort);
  const start = (params.page - 1) * params.per_page;
  return { ...first, data: { data: ordered.slice(start, start + params.per_page), meta: { total } } };
}
